import React, { useEffect, useRef } from 'react';
import HomeHeader from '@/components/HomeHeader';
import Footer from '@/components/homepage/Footer';
import { usePageMeta } from '@/hooks/usePageMeta';
import TableOfContents from '@/components/resources/TableOfContents';
import EmailCapture from '@/components/resources/EmailCapture';

const TITLE = 'How to Write a Missionary Thank-You Letter Donors Actually Remember';
const DESCRIPTION =
    "A practical guide to writing donor thank-you letters that build long-term missionary support — what to say in the first 48 hours, how to make a gift feel specific instead of generic, and a simple template you can reuse.";

const JSON_LD = {
    '@context': 'https://schema.org',
    '@graph': [
        {
            '@type': 'Article',
            headline: TITLE,
            description: DESCRIPTION,
            image: 'https://devotionfund.com/resources/cover23.jpg',
            datePublished: '2026-07-27',
            dateModified: '2026-07-27',
            author: { '@type': 'Organization', name: 'Devotion Fund', url: 'https://devotionfund.com' },
            publisher: {
                '@type': 'Organization',
                name: 'Devotion Fund',
                logo: { '@type': 'ImageObject', url: 'https://devotionfund.com/logo.png' },
            },
            mainEntityOfPage: {
                '@id': 'https://devotionfund.com/resources/how-to-write-a-missionary-thank-you-letter',
                '@type': 'WebPage',
            },
        },
        {
            '@type': 'FAQPage',
            mainEntity: [
                {
                    '@type': 'Question',
                    name: 'How quickly should a missionary send a thank-you letter after a donation?',
                    acceptedAnswer: {
                        '@type': 'Answer',
                        text: 'Within 48 hours for a one-time gift, and within the first week for a new monthly supporter. Speed reads as genuine gratitude — a thank-you that arrives weeks later, even if warmly written, feels like an afterthought rather than a real response to the gift.',
                    },
                },
                {
                    '@type': 'Question',
                    name: 'What makes a missionary thank-you letter feel generic instead of genuine?',
                    acceptedAnswer: {
                        '@type': 'Answer',
                        text: 'Naming the amount and effect instead of a vague blanket thank-you. "Thank you for your generosity" could be sent to anyone; "your gift covers two weeks of language school tuition" tells this specific donor what their specific gift did, which is what makes people give again.',
                    },
                },
                {
                    '@type': 'Question',
                    name: 'Should a thank-you letter for a monthly donor be different from a one-time gift?',
                    acceptedAnswer: {
                        '@type': 'Answer',
                        text: 'Yes. A one-time gift thank-you should close the loop on a specific need. A new monthly donor thank-you should welcome them into an ongoing relationship — what regular updates they can expect and how often — since the goal isn\'t just gratitude, it\'s setting the tone for a recurring relationship.',
                    },
                },
                {
                    '@type': 'Question',
                    name: 'How often should missionaries thank long-term recurring donors, not just first-time givers?',
                    acceptedAnswer: {
                        '@type': 'Answer',
                        text: "At least once or twice a year beyond the automatic thank-you, separate from a regular newsletter — a short, personal note timed to a milestone (a year of partnership, a project their support funded) reminds long-time donors they're still seen, not just billed.",
                    },
                },
            ],
        },
    ],
};

export default function HowToWriteAMissionaryThankYouLetter() {
    usePageMeta(TITLE, DESCRIPTION, { path: '/resources/how-to-write-a-missionary-thank-you-letter' });

    const articleRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const el = document.createElement('script');
        el.type = 'application/ld+json';
        el.setAttribute('data-thank-you-letter-jsonld', 'true');
        el.textContent = JSON.stringify(JSON_LD);
        document.head.appendChild(el);
        return () => {
            el.remove();
        };
    }, []);

    return (
        <div className="min-h-screen bg-white flex flex-col">
            <HomeHeader />
            <div className="w-full h-64 md:h-80 bg-gray-100 overflow-hidden">
                <img src="/resources/cover23.jpg" alt="A handwritten thank-you note next to an envelope, representing donor gratitude" className="w-full h-full object-cover" />
            </div>
            <div className="max-w-3xl mx-auto px-4 py-12 w-full">
                <h1 className="text-3xl font-bold mb-6">{TITLE}</h1>
                <TableOfContents targetRef={articleRef} />
                <div className="prose max-w-none" ref={articleRef}>
                    <p>Most of the advice on missionary support focuses on the ask — the letter, the page, the follow-up call. Almost nobody talks about what happens after someone actually gives, which is a mistake, because the thank-you is the single biggest lever for whether that donor gives again. Here's how to write one that a donor actually remembers, plus a template you can reuse for every gift that comes in.</p>

                    <h2>Why the thank-you matters more than the ask</h2>

                    <p>The ask gets a donor to give once. The thank-you decides whether they give twice. A donor who feels genuinely seen after their first gift is far more likely to become a recurring supporter than one who gives, hears nothing specific back, and quietly assumes their gift landed in a general pile. Treat the thank-you as part of the fundraising process, not an administrative afterthought that happens after the "real" work is done.</p>

                    <h2>Send it within 48 hours</h2>

                    <p>Speed is itself a signal. A thank-you that lands within a day or two of the gift reads as a genuine, immediate response — the same note sent three weeks later, even if it's warmly written, feels like it came from an obligation rather than real gratitude. If you're managing dozens of gifts, a short same-day note beats a longer one you send once a month in a batch.</p>

                    <h2>Name the amount, and name the effect</h2>

                    <p>The single biggest upgrade you can make to a thank-you letter is replacing a vague blanket line with something specific to this gift. Compare these two:</p>

                    <p><em>Generic:</em> "Thank you so much for your generosity and support of my ministry."</p>

                    <p><em>Specific:</em> "Your $75 gift covers about two weeks of language school tuition — I'll be able to sit in class instead of worrying about this month's bill."</p>

                    <p>The second version could only have been written to this donor about this gift. That specificity is what makes a thank-you feel real instead of templated, even when you're sending dozens of them and reusing a structure underneath.</p>

                    <h2>A simple four-part template</h2>

                    <p>Whether it's an email, a text, or a handwritten card, the same four parts hold up for almost any gift:</p>

                    <p><strong>1. Thank them by name, immediately.</strong> "Thank you, Sarah" beats "Dear Supporter" every time — use their actual name in the first line, not just the greeting.</p>

                    <p><strong>2. Name the gift and what it does.</strong> The amount (or "your gift," if you'd rather not restate the number) and one concrete sentence on what it funds — tuition, a flight, a month of rent, a ministry supply.</p>

                    <p><strong>3. One sentence of real context.</strong> A short detail about what's happening on the field right now — not a full update, just enough to make the thank-you feel current rather than a stock message you'd send in any month.</p>

                    <p><strong>4. A warm, low-pressure close.</strong> Something like "I'm grateful you're part of this" or "Praying for you as much as I'm grateful for you" — no new ask, no reminder to give again. A thank-you that quietly pivots into another appeal undercuts its own sincerity.</p>

                    <h2>New monthly donors need a different thank-you than one-time givers</h2>

                    <p>A one-time gift thank-you closes the loop on a specific need. A brand-new monthly donor thank-you has a different job: welcoming them into an ongoing relationship. Tell them plainly what to expect — "you'll hear from me with a short update every month" — so the relationship starts with clear expectations instead of silence until the next newsletter shows up unannounced. This is also the moment to point them toward how they'll keep hearing from you; see our guide on <a href="/resources/how-to-write-a-prayer-letter-people-actually-read">writing updates people actually read</a> for the format that keeps recurring donors engaged long-term.</p>

                    <h2>Don't forget your long-term recurring donors</h2>

                    <p>It's easy to thank a first-time giver well and then let a two-year monthly donor fade into "just another line in the budget." Long-term donors need to be thanked too — not with the automated receipt their bank generates, but with a short, personal note once or twice a year, timed to something real: the anniversary of when they started giving, or a project their cumulative support actually funded. A recurring donor who hasn't heard anything personal in a year is a donor at real risk of quietly lapsing, even if the giving itself hasn't stopped yet.</p>

                    <h2>Match the format to the gift size and channel</h2>

                    <ul>
                        <li><strong>Small or first-time gifts:</strong> a prompt, warm email or text is enough — fast beats formal here.</li>
                        <li><strong>Larger gifts or founding supporters:</strong> a handwritten card adds a level of effort that a larger gift deserves, even if it arrives a day or two later than an email would.</li>
                        <li><strong>Recurring monthly donors:</strong> an automated receipt for the transaction, plus a genuinely personal note at the milestones above — the two aren't a replacement for each other.</li>
                    </ul>

                    <h2>Keep a simple system so nothing slips</h2>

                    <p>A thank-you strategy only works if it actually happens for every gift, not just the ones you remember. A simple spreadsheet or even a notes app list — donor name, gift date, amount, whether you've sent a thank-you — is enough to make sure no one falls through. This is the same discipline behind a sustainable update rhythm; see our <a href="/resources/free-missionary-newsletter-template">newsletter template</a> for a format built to keep going without burning you out.</p>

                    <p>If you're building or growing a support page, a platform that shows you each gift as it comes in — who gave, how much, one-time or recurring — makes this whole habit far easier to keep up than reconstructing it from bank statements once a month. <a href="/">Start a page on Devotion Fund</a> to see every gift the moment it lands.</p>

                    <h2>Frequently asked questions</h2>

                    <p><strong>How quickly should I send a thank-you letter?</strong><br />
                    Within 48 hours for a one-time gift, within the first week for a new monthly supporter — speed signals genuine gratitude.</p>

                    <p><strong>What makes a thank-you feel generic?</strong><br />
                    A vague blanket line instead of naming the specific amount and what it funds — specificity is what makes it feel real.</p>

                    <p><strong>Should monthly donors get a different thank-you than one-time givers?</strong><br />
                    Yes — welcome them into an ongoing relationship and set expectations for how often they'll hear from you, rather than just closing the loop on one gift.</p>

                    <p><strong>How often should I thank long-term recurring donors?</strong><br />
                    At least once or twice a year with a short, personal note tied to a real milestone, beyond your regular newsletter.</p>

                    <h2>Next reads on Devotion Fund</h2>
                    <ul>
                        <li><a href="/resources/how-to-write-a-prayer-letter-people-actually-read">How to Write a Prayer Letter People Actually Read</a></li>
                        <li><a href="/resources/free-missionary-newsletter-template">Free Missionary Newsletter Template + 3 Real Examples Teardown</a></li>
                        <li><a href="/resources/how-to-write-a-missionary-support-letter">How to Write a Missionary Support Letter That Actually Gets You Funded</a></li>
                    </ul>

                    <h2>About the author</h2>
                    <p>We're the team behind <a href="/">Devotion Fund</a>. This guide came out of watching which missionaries kept their donors for years and which ones quietly lost them — the difference was almost always in what happened in the days right after a gift, not in the ask itself.</p>
                    <p><small><em>Last updated: 2026-07-27.</em></small></p>
                </div>
                <div className="mt-8">
                    <EmailCapture page="/resources/how-to-write-a-missionary-thank-you-letter" />
                </div>
            </div>
            <Footer />
        </div>
    );
}
